/* OnSumn — Taste Profile (first-visit full-screen quiz) */

const TASTE_VIBES = ['Music', 'Cards', 'Art', 'Food', 'Nightlife', 'Community'];
const TASTE_SOUNDS = ['EDM', 'R&B', 'Hip-Hop', 'Rock', 'Jazz', 'Pop', 'Latin', 'Indie'];

const VIBE_COLORS = {
  'Music': '#D57800',
  'Cards': '#7A99AC',
  'Art': '#FF4F00',
  'Food': '#395542',
  'Nightlife': '#7C5BBC',
  'Community': '#5A7A68',
};

const SOUND_COLORS = {
  'EDM': '#FF4F00',
  'R&B': '#D57800',
  'Hip-Hop': '#5A7A68',
  'Rock': '#7A99AC',
  'Jazz': '#D57800',
  'Pop': '#FFB778',
  'Latin': '#FF4F00',
  'Indie': '#7A99AC',
};

/* ── Chip component (reuses SelectorChips pattern) ──
   G2a (2026-07-28): aria-pressed (toggle semantics for screen readers and
   the re-entry gates), darkMode actually consumed (it was accepted and
   ignored — every surface hardcoded the white family), reduced-motion
   honored on the scale/width/opacity transitions. Selected treatment
   (color fill + label + check) is identical in both modes. The label/check
   ink is picked per fill by categoryTextColor: white was 1.71–3.30:1 on the
   orange, amber, fog and peach fills; the picked ink is 4.75:1+ on all seven. */
function TasteChip({ label, isSelected, color, darkMode, onClick }) {
  const reduce = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <button onClick={onClick} aria-pressed={isSelected} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: 6, padding: '12px 22px', minHeight: 48,
      borderRadius: 100,
      fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-body)',
      letterSpacing: '0.04em', textTransform: 'uppercase',
      cursor: 'pointer',
      border: isSelected ? `2px solid ${color}`
        : (darkMode ? '2px solid rgba(255,255,255,0.12)' : '2px solid rgba(0,0,0,0.14)'),
      background: isSelected ? color
        : (darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
      color: isSelected
        ? ((typeof window !== 'undefined' && window.categoryTextColor) ? window.categoryTextColor(color) : '#10110F')
        : (darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.62)'),
      transition: reduce ? 'none' : 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
      transform: (isSelected && !reduce) ? 'scale(1.05)' : 'scale(1)',
      boxShadow: isSelected ? `0 4px 16px ${color}44` : 'none',
    }}>
      <span>{label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        width: isSelected ? 16 : 0,
        opacity: isSelected ? 1 : 0,
        overflow: 'hidden',
        transition: reduce ? 'none' : 'width 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s',
      }}>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
          style={{
            transform: isSelected ? 'scale(1)' : 'scale(0)',
            transition: reduce ? 'none' : 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
          <path d="M5 10.5L9 14.5L15 7.5" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </button>
  );
}

/* ── Full-screen taste profile overlay ──
   G2a (2026-07-28): the picker is a DOOR now, not a one-way gate.
     · PRE-FILLS from getSavedTaste() — reopening shows current picks, so
       re-entry cannot silently overwrite a returning user's profile.
     · CANCEL (text button, ✕, or Esc) closes without touching storage.
     · CLEAR is one neutral control that erases onsumn-taste and nothing
       else — no confirm chain, no retention offer (confirmshaming is
       banned by name: loss-framing-gate §2.4, enforced through
       LOSS_COPY_BANNED_LEXICON + tests/taste-reentry.test.js).
     · Real dialog semantics: role=dialog, aria-modal, labelled heading,
       focus trap, Esc-to-cancel (capture — flows Spotlight precedent),
       focus handed back to the opener on close (falls back to the
       [data-taste-reentry] pill when the opener unmounted — the nudge
       sheet does exactly that when it opens the picker).
     · Finally reads its own darkMode prop — TasteNudgeSheet below was
       always the working pattern. Flat ink dark / warm cream light.
   First-run semantics unchanged: submit writes picks; "Skip — I want it
   all" stays terminal and byte-identical. Cancel/Clear replace Skip only
   when a completed profile exists — pre-G2a a returning user tapping
   Skip would have ERASED their taste. */
function TasteProfileOverlay({ darkMode, onComplete }) {
  const saved = React.useMemo(() => getSavedTaste(), []);
  const hasExisting = !!(saved && !saved.skipped);
  const [vibes, setVibes] = React.useState(() =>
    (hasExisting && Array.isArray(saved.vibes)) ? [...saved.vibes] : []);
  const [sounds, setSounds] = React.useState(() =>
    (hasExisting && Array.isArray(saved.sounds)) ? [...saved.sounds] : []);
  const [fadeOut, setFadeOut] = React.useState(false);
  const reduce = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rootRef = React.useRef(null);
  const headingRef = React.useRef(null);
  const closingRef = React.useRef(false);
  const cancelRef = React.useRef(null);

  const toggleVibes = (v) => setVibes(prev =>
    prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
  );
  const toggleSounds = (s) => setSounds(prev =>
    prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
  );

  /* Every exit funnels through here: fade unless reduced-motion, then
     notify. index-main's onComplete re-reads storage, so cancel/clear
     need no extra plumbing — they simply don't write / remove, then
     close. */
  const finish = (result) => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (reduce) { onComplete(result); return; }
    setFadeOut(true);
    setTimeout(() => onComplete(result), 400);
  };

  /* Account-lite write-through (2026-08-26). This editor stays the ONE editor
     for this datum; it simply pushes the store it already writes. No-ops when
     nothing is attached, so the signed-out path is byte-unchanged. The rail
     is NOT involved: it keeps reading local onsumn-taste through the same
     readers it always did — account-lite adds one writer and zero readers. */
  const pushTaste = () => {
    const acct = typeof window !== 'undefined' ? window.OnSumnAccount : null;
    if (!acct || !acct.isAttached()) return;
    const p = acct.pushField('taste');
    if (p && p.catch) p.catch(() => {});
  };
  const attachedNow = typeof window !== 'undefined' && window.OnSumnAccount
    && window.OnSumnAccount.isAttached();

  const handleSubmit = () => {
    const taste = { v: TASTE_SCHEMA_V, vibes, sounds, timestamp: Date.now() };
    localStorage.setItem('onsumn-taste', JSON.stringify(taste));
    pushTaste();
    finish(taste);
  };

  const handleSkip = () => {
    localStorage.setItem('onsumn-taste', JSON.stringify({ v: TASTE_SCHEMA_V, vibes: [], sounds: [], skipped: true, timestamp: Date.now() }));
    pushTaste();
    finish(null);
  };

  const handleCancel = () => { finish(null); };            // storage untouched

  const handleClear = () => {                              // neutral erase — onsumn-taste only
    try { localStorage.removeItem('onsumn-taste'); } catch {}
    // No resurrection: an erase that only cleared the local copy would come
    // back from the server on the next load. Scope is announced in advance by
    // the sub-line beside this control while a setup is attached.
    const acct = typeof window !== 'undefined' ? window.OnSumnAccount : null;
    if (acct && acct.isAttached()) {
      const p = acct.removeField('taste');
      if (p && p.catch) p.catch(() => {});
    }
    finish(null);
  };
  cancelRef.current = handleCancel;

  /* Focus: remember the opener, take focus on entry, hand it back on
     unmount. WCAG 2.4.3 — Iris gate. */
  React.useEffect(() => {
    const opener = document.activeElement;
    if (headingRef.current) headingRef.current.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') { opener.focus(); return; }
      const pill = document.querySelector('[data-taste-reentry] button');
      if (pill) pill.focus();
    };
  }, []);

  /* Esc cancels; Tab is trapped inside the dialog. */
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); cancelRef.current(); return; }
      if (e.key !== 'Tab' || !rootRef.current) return;
      const f = rootRef.current.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  const quietText = darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.55)';
  const quietTextHover = darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.8)';

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="taste-overlay-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '48px 24px',
        // Side-stripe replacement spec bundle (Zora 2026-05-26): the
        // dual radial-gradient ambient overlay was the same AI-aesthetic
        // family as the venue side-stripes. Replaced with a flat brand
        // surface — the overlay was decorative, not informational, and
        // the brutalist-editorial brief prefers flat ink over ambient
        // gradients. G2a: the flat surface finally branches on darkMode
        // (flat ink dark / warm cream light — venue light precedent).
        background: darkMode ? '#0A0A0A' : '#FAF7F1',
        opacity: fadeOut ? 0 : 1,
        transition: reduce ? 'none' : 'opacity 0.4s ease',
        overflowY: 'auto',
      }}>
      {/* Close = cancel, storage untouched. 48px floor (Iris). */}
      <button
        type="button"
        className="focus-double-ring"
        onClick={handleCancel}
        aria-label="Close without saving"
        style={{
          position: 'absolute', top: 16, right: 16,
          width: 48, height: 48, borderRadius: 100,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-body)', fontSize: 17, fontWeight: 700, lineHeight: 1,
          background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
          color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
          border: 'none', cursor: 'pointer', zIndex: 3,
        }}
      >✕</button>

      {/* Ambient glow — dark mode only (color-dodge over cream reads as a smear) */}
      {darkMode && (
        <div style={{
          position: 'absolute', top: '-10%', left: '-5%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'rgba(206,128,73,0.12)', filter: 'blur(120px)',
          mixBlendMode: 'color-dodge', pointerEvents: 'none',
        }} />
      )}

      <div style={{
        position: 'relative', zIndex: 2,
        maxWidth: 560, width: '100%', textAlign: 'center',
      }}>
        <h1 id="taste-overlay-title" ref={headingRef} tabIndex={-1} style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 800, letterSpacing: '-0.03em',
          color: darkMode ? '#fff' : '#0A0A0A', marginBottom: 12, lineHeight: 1.1,
          outline: 'none',
        }}>What Are You Into?</h1>

        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 16,
          color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
          marginBottom: 40, lineHeight: '24px',
        }}>Tap what you're into. We'll surface those first.</p>

        {/* Vibes */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: quietText, marginBottom: 12,
          }}>Vibe</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {TASTE_VIBES.map(v => (
              <TasteChip key={v} label={v} isSelected={vibes.includes(v)}
                color={VIBE_COLORS[v]} darkMode={darkMode} onClick={() => toggleVibes(v)} />
            ))}
          </div>
        </div>

        {/* Sounds */}
        <div style={{ marginBottom: 36 }}>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: quietText, marginBottom: 12,
          }}>Sound</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {TASTE_SOUNDS.map(s => (
              <TasteChip key={s} label={s} isSelected={sounds.includes(s)}
                color={SOUND_COLORS[s]} darkMode={darkMode} onClick={() => toggleSounds(s)} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <button onClick={handleSubmit} style={{
          display: 'inline-block', padding: '16px 48px',
          borderRadius: 100,
          background: 'var(--international-orange)', color: '#10110F',
          fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700,
          letterSpacing: '0.03em', textTransform: 'uppercase',
          border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 24px rgba(255,79,0,0.35)',
          transition: reduce ? 'none' : 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { if (reduce) return; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(255,79,0,0.45)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(255,79,0,0.35)'; }}
        >Let's Get It</button>

        {hasExisting ? (
          /* Returning user: Cancel (no write) + one neutral Clear. Copy is
             a DRAFT — Rhea owns final wording (flagged on the PR). */
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" className="focus-double-ring" onClick={handleCancel} style={{
              fontFamily: 'var(--font-body)', fontSize: 13, minHeight: 48,
              color: quietText, cursor: 'pointer', textDecoration: 'none',
              transition: reduce ? 'none' : 'color 0.2s',
              background: 'none', border: 'none', padding: '0 8px',
            }}
            onMouseEnter={e => e.currentTarget.style.color = quietTextHover}
            onMouseLeave={e => e.currentTarget.style.color = quietText}
            >Cancel</button>
            <button type="button" className="focus-double-ring" onClick={handleClear} style={{
              fontFamily: 'var(--font-body)', fontSize: 13, minHeight: 48,
              color: quietText, cursor: 'pointer',
              textDecoration: 'underline', textUnderlineOffset: 3,
              transition: reduce ? 'none' : 'color 0.2s',
              background: 'none', border: 'none', padding: '0 8px',
            }}
            onMouseEnter={e => e.currentTarget.style.color = quietTextHover}
            onMouseLeave={e => e.currentTarget.style.color = quietText}
            >Clear my picks</button>
            {attachedNow && (
              /* G6 scope line — the same string the /flows FORGET control
                 carries, for the same reason: while attached this erase
                 reaches past the device, and the user gets to know that
                 before the tap, not after. */
              <p data-afy-scope style={{
                width: '100%', textAlign: 'center', margin: '2px 0 0',
                fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
                lineHeight: 1.5, color: quietText,
              }}>Clears it here and from your setup.</p>
            )}
          </div>
        ) : (
          <button onClick={handleSkip} style={{
            display: 'block', marginTop: 20, minHeight: 48,
            fontFamily: 'var(--font-body)', fontSize: 13,
            color: quietText,
            cursor: 'pointer', textDecoration: 'none',
            transition: reduce ? 'none' : 'color 0.2s',
            background: 'none', border: 'none', padding: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.color = quietTextHover}
          onMouseLeave={e => e.currentTarget.style.color = quietText}
          >Skip — I want it all</button>
        )}
      </div>
    </div>
  );
}

/* ── Lifecycle constants (G2b, 2026-07-28) ──
   feedback_data_lifecycle: data must ripen → expire → renew. onsumn-taste
   shipped with a `timestamp` nothing read, no schema version and no TTL —
   what it had instead was ACCIDENTAL decay (the vocabulary filters below
   silently drop saved picks whenever the TASTE_* literals change: death
   with no death certificate). G2b replaces the accident with a dated
   expiry and a versioned shape:
     { v: 1, vibes: [], sounds: [], skipped?: true, timestamp: epoch-ms }
   TASTE_TTL_MS is a PROPOSAL — Marco ruled there must be a window; the
   NUMBER is Mav's call (flagged on the PR). Window-attached for the
   lifecycle gates and the G3 disclosure panel. */
const TASTE_SCHEMA_V = 1;
const TASTE_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days — FLAGGED FOR MAV

/* ── Helper: get saved taste from localStorage ──
   Contract (G2b): unreadable/wrong-shape → null, never throws. A COMPLETED
   profile older than TASTE_TTL_MS — or one that cannot be dated — is
   ABSENT: null here, plain chronological feed, no rail, picker offerable
   again. The timestamp is READ, never rewritten: no sliding window (a
   profile that never expires wearing better paperwork). A terminal skip
   never expires — a refusal is not personalization, and expiring it would
   re-arm the ask G2a keeps terminal. Missing/unrecognised `v` = old shape:
   migrated in place ONCE (v stamped, vibe map applied, ORIGINAL timestamp
   preserved byte-for-byte); garbage is never "fixed" in place. */
function getSavedTaste() { return readSavedTasteCore(true); }

/* Non-migrating entry to the SAME parser — for surfaces whose contract is
   "reading writes nothing," i.e. the WHAT THIS BROWSER KNOWS panel (G3a,
   Atlas hole 2). One parser, two doors: getSavedTaste may stamp v on a
   legacy blob; peekSavedTaste never touches storage. */
function peekSavedTaste() { return readSavedTasteCore(false); }

function readSavedTasteCore(migrate) {
  try {
    const raw = localStorage.getItem('onsumn-taste');
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    data.skipped = data.skipped === true;

    /* Expiry gate — completed profiles only; skips are refusal records. */
    if (!data.skipped) {
      const ts = (typeof data.timestamp === 'number' && Number.isFinite(data.timestamp))
        ? data.timestamp : null;
      if (ts === null) return null;                 // undatable = absent
      if (Date.now() - ts > TASTE_TTL_MS) return null; // expired = absent everywhere
    }

    /* Migrate old vibe names */
    const VIBE_MAP = { 'Concerts': 'Music', 'Culture': 'Community' };
    const validVibes = new Set(TASTE_VIBES);
    const validSounds = new Set(TASTE_SOUNDS);
    data.vibes = Array.isArray(data.vibes)
      ? data.vibes.map(v => VIBE_MAP[v] || v).filter(v => typeof v === 'string' && validVibes.has(v))
      : [];
    data.sounds = Array.isArray(data.sounds)
      ? data.sounds.filter(s => typeof s === 'string' && validSounds.has(s))
      : [];

    /* Schema migration — the ONLY write a read may perform, and it fires
       once: a second read sees v === TASTE_SCHEMA_V and writes nothing
       (gate: read twice → storage byte-identical). Timestamp value is
       carried over unchanged — migration must not mint freshness. */
    if (data.v !== TASTE_SCHEMA_V) {
      data.v = TASTE_SCHEMA_V;
      if (migrate) {
        try {
          const stamped = { v: TASTE_SCHEMA_V, vibes: data.vibes, sounds: data.sounds };
          if (data.skipped) stamped.skipped = true;
          if (typeof data.timestamp === 'number' && Number.isFinite(data.timestamp)) {
            stamped.timestamp = data.timestamp;
          }
          localStorage.setItem('onsumn-taste', JSON.stringify(stamped));
        } catch {}
      }
    }
    return data;
  } catch { return null; }
}

/* ── Helper: compute taste boost for an event ── */
function tasteBoost(event, taste) {
  if (!taste || taste.skipped) return 0;
  let boost = 0;

  // Category/vibe match
  if (taste.vibes && taste.vibes.length > 0) {
    if (taste.vibes.some(v => v.toUpperCase() === event.category)) boost += 20;
  }

  // Sub-genre/sound match
  if (taste.sounds && taste.sounds.length > 0 && event.subgenres && event.subgenres.length > 0) {
    const matches = taste.sounds.filter(s => event.subgenres.includes(s));
    boost += matches.length * 15;
  }

  return boost;
}

/* ── Value-first taste ask (reciprocity — research doc §3) ──
   The full-screen overlay above no longer mounts on first paint. Instead:
     1. TasteNudgeSheet — non-modal, dismissible invite the App shows only
        AFTER a real engagement signal (user-input scroll past the first
        carousel row, or a card interaction — programmatic scrolls like the
        #event- deep-link highlight don't count; see index-main.jsx).
     2. TuneWavePill — permanent re-entry next to the view toggles while no
        COMPLETED taste exists, so "skip" is never a dead end.
   Lifecycle (data must ripen → expire → renew):
     localStorage 'onsumn-taste-nudge' {shownCount, lastShownAt} — dismissal
     counter + last-shown stamp. Auto-show: at most once per visit
     (sessionStorage gate), at most 3 dismissals lifetime; after 3, the
     sheet never auto-shows again and only the pill remains. Completing the
     picker writes 'onsumn-taste' (existing semantics untouched), which
     disarms everything. Saved/skipped users never see the sheet. */

const TASTE_NUDGE_KEY = 'onsumn-taste-nudge';
const TASTE_NUDGE_VISIT_KEY = 'onsumn-taste-nudge-visit';
const TASTE_NUDGE_MAX_DISMISSALS = 3;

function readTasteNudgeState() {
  try {
    const raw = JSON.parse(localStorage.getItem(TASTE_NUDGE_KEY));
    return {
      shownCount: raw && typeof raw.shownCount === 'number' ? raw.shownCount : 0,
      lastShownAt: raw && typeof raw.lastShownAt === 'number' ? raw.lastShownAt : null,
    };
  } catch { return { shownCount: 0, lastShownAt: null }; }
}

function canAutoShowTasteNudge() {
  try { if (sessionStorage.getItem(TASTE_NUDGE_VISIT_KEY)) return false; } catch {}
  return readTasteNudgeState().shownCount < TASTE_NUDGE_MAX_DISMISSALS;
}

function markTasteNudgeShown() {
  try { sessionStorage.setItem(TASTE_NUDGE_VISIT_KEY, '1'); } catch {}
  try {
    const s = readTasteNudgeState();
    localStorage.setItem(TASTE_NUDGE_KEY, JSON.stringify({ shownCount: s.shownCount, lastShownAt: Date.now() }));
  } catch {}
}

function markTasteNudgeDismissed() {
  try {
    const s = readTasteNudgeState();
    localStorage.setItem(TASTE_NUDGE_KEY, JSON.stringify({ shownCount: s.shownCount + 1, lastShownAt: s.lastShownAt || Date.now() }));
  } catch {}
}

/* Non-modal invite sheet. Dialog band z-index (1600) — deliberately NOT the
   9990+ modal band: the feed behind stays scrollable and clickable; only
   the full picker (opened from here) takes the 99999 takeover. No focus
   steal on entry (role=status + aria-live announces it); Esc dismisses;
   prefers-reduced-motion renders it in place with no slide. */
function TasteNudgeSheet({ darkMode, onOpen, onDismiss }) {
  const reduce = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(max-width: 767.98px)').matches;
  const [entered, setEntered] = React.useState(reduce); // reduced motion → appear instantly

  React.useEffect(() => {
    if (reduce) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [reduce]);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', zIndex: 1600,
        ...(mobile
          ? { left: 16, right: 16, bottom: 'max(16px, env(safe-area-inset-bottom))' }
          : { right: 20, bottom: 'max(20px, env(safe-area-inset-bottom))', width: 340 }),
        ...glassStyle(darkMode, 'panel'),
        background: darkMode
          ? 'linear-gradient(145deg, rgba(18,20,20,0.96), rgba(10,10,10,0.97))'
          : 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,248,248,0.97))',
        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
        borderRadius: 18,
        padding: '16px 18px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        transform: entered ? 'translateY(0)' : 'translateY(14px)',
        opacity: entered ? 1 : 0,
        transition: reduce ? 'none' : 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease',
      }}
    >
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 800,
        letterSpacing: '0.02em', textTransform: 'uppercase',
        color: darkMode ? '#fff' : '#0A0A0A', marginBottom: 4,
      }}>What's Your Wave?</div>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 12.5, lineHeight: 1.45,
        color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
        marginBottom: 12,
      }}>Tell us what you're into — we'll pull your kind of nights to the front.</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          className="focus-double-ring"
          onClick={onOpen}
          style={{
            minHeight: 44, padding: '0 18px', borderRadius: 100,
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            background: 'var(--international-orange)', color: '#10110F',
            border: 'none', cursor: 'pointer',
          }}
        >Tune My Wave</button>
        <button
          type="button"
          className="focus-double-ring"
          onClick={onDismiss}
          style={{
            minHeight: 44, padding: '0 14px', borderRadius: 100,
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            background: 'transparent',
            color: darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
            border: 'none', cursor: 'pointer',
          }}
        >Not now</button>
      </div>
    </div>
  );
}

/* Permanent re-entry pill — sits by the Map/Split/Grid/Cal toggles in EVERY
   taste state (G2a: a preference you cannot revisit is one you cannot
   revoke). hasTaste switches only the label: the no-taste text is
   byte-identical to the original ask; the has-taste text is a DRAFT —
   Rhea owns the final wording (flagged on the PR). Amber family
   (MERCH-chip precedent): orange stays reserved for primary actions. */
function TuneWavePill({ darkMode, hasTaste, onClick }) {
  return (
    <button
      type="button"
      className="focus-double-ring"
      onClick={onClick}
      aria-label={hasTaste
        ? 'Retune your wave — edit or clear your picks'
        : 'Tune your wave — pick what you\'re into'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        minHeight: 44, padding: '0 16px', borderRadius: 100,
        fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'var(--amber-gold-ink)',
        background: darkMode ? 'rgba(213,120,0,0.12)' : 'rgba(213,120,0,0.14)',
        border: '1px solid rgba(213,120,0,0.5)',
        cursor: 'pointer',
      }}
    >{hasTaste ? 'Retune Your Wave' : 'Tune Your Wave'}</button>
  );
}

TasteProfileOverlay.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
};

TasteNudgeSheet.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

TuneWavePill.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  hasTaste: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

window.TasteProfileOverlay = TasteProfileOverlay;
window.getSavedTaste = getSavedTaste;
window.peekSavedTaste = peekSavedTaste; // non-migrating read — disclosure panel (G3a)
window.tasteBoost = tasteBoost;
window.TASTE_TTL_MS = TASTE_TTL_MS;       // lifecycle gates + G3 panel read these
window.TASTE_SCHEMA_V = TASTE_SCHEMA_V;
window.TASTE_SOUNDS = TASTE_SOUNDS;
window.TasteNudgeSheet = TasteNudgeSheet;
window.TuneWavePill = TuneWavePill;
window.readTasteNudgeState = readTasteNudgeState; // G3a: disclosure panel reads through the existing reader
window.canAutoShowTasteNudge = canAutoShowTasteNudge;
window.markTasteNudgeShown = markTasteNudgeShown;
window.markTasteNudgeDismissed = markTasteNudgeDismissed;
window.SOUND_COLORS = SOUND_COLORS;
